import { setRetailPriceErrors, setWholesalePriceErrors, updateProductErrorsFromVariations } from '@/utils/product/errors';
import { shapeFaireDistanceUnitForApp, shapeFaireMassUnitForApp, shapeKeyDate } from '@/utils/product/shapeAttributes';
import { clearErrors, clearVariationsCombinationErrors } from '@/utils/errors';
import { getError, getNumberValue, getPriceValue } from '@/utils/bulkEdit';
import { escapeRegex } from '@/utils/string';
import { getSize } from '@/utils/iterable/getSize';
import { void0 } from '@/utils/void0';

import { AMOUNT, PRICE_API_KEY_TO_ATTRIBUTE_CURRENCY_MAP, CURRENCIES, CURRENCY, ERROR, OVERSET, REQUIRED, VALUE } from '@/constants/product';
import { COMBINATIONS, INFLUENCE, VARIANTS } from '@/constants/variations';
import { EBAY, ETSY, FAIRE, SHOPIFY } from '@/constants/channels';
import { OPERATIONS, PRICE_TYPE } from '@/constants/bulkEdit';
import { ATTRIBUTE } from '@/constants/attributes';
import { EMPTY } from '@/constants';
import { FORM } from '@/constants/forms';

const NO_CHANGES = 'no_changes';

function getAttributeByOperation(operationType) {
  if (/^barcode/.test(operationType)) {
    return ATTRIBUTE.BARCODE;
  }

  if (/^comapreAtPrice/.test(operationType)) {
    return ATTRIBUTE.CAP;
  }

  if (/^cost/.test(operationType)) {
    return ATTRIBUTE.CPI;
  }

  if (/^countryCode/.test(operationType)) {
    return ATTRIBUTE.COUNTRY_CODE;
  }

  if (/^hsCode/.test(operationType)) {
    return ATTRIBUTE.HS_CODE;
  }

  if (/^price/.test(operationType)) {
    return ATTRIBUTE.PRICE;
  }

  if (/^sku/.test(operationType)) {
    return ATTRIBUTE.SKU;
  }

  if (/^gtin/.test(operationType)) {
    return ATTRIBUTE.GTIN;
  }

  return undefined;
}

function updateEbayInventory({ operation, product: source }) {
  const channel = source.get('channel');
  const operationType = operation.get('type');

  function bulkEdit({ attribute, updateInventory = void0, updateCombination = void0 }) {
    let product = source;
    const size = getSize(product.getIn([FORM.VARIATIONS, COMBINATIONS]));
    const productInventory = updateInventory(product.get(FORM.INVENTORY));

    if (productInventory === ERROR) return source;

    function reduceCombinationsToDuplicate(result, combination) {
      if (result.duplicate) return result;

      const sku = combination.get(ATTRIBUTE.SKU);

      if (sku) {
        if (result.sku.has(sku)) {
          result.duplicate = true;
        } else {
          result.sku = result.sku.add(sku);
        }
      }

      return result;
    }

    if (productInventory !== NO_CHANGES) {
      product = clearErrors({
        errors: [attribute],
        path: FORM.INVENTORY,
        product: product.set(FORM.INVENTORY, productInventory),
      });
    }

    if (size) {
      for (let combinationIndex = 0; combinationIndex < size; combinationIndex++) {
        const combination = updateCombination(product.getIn([FORM.VARIATIONS, COMBINATIONS, combinationIndex]));

        if (combination === ERROR) return source;

        if (combination === NO_CHANGES) continue;

        product = clearVariationsCombinationErrors({
          errors: [attribute],
          combinationIndex,
          product: product.setIn([FORM.VARIATIONS, COMBINATIONS, combinationIndex], combination),
        });
      }
    }

    if (
      size > 0 &&
      attribute === ATTRIBUTE.SKU &&
      product.getIn([FORM.VARIATIONS, COMBINATIONS]).reduce(reduceCombinationsToDuplicate, { sku: EMPTY.SET }).duplicate
    ) {
      return source;
    }

    return product;
  }

  switch (operationType) {
    case OPERATIONS[EBAY].PRICE.CHANGE_TO:
    case OPERATIONS[EBAY].PRICE.DECREASE_BY:
    case OPERATIONS[EBAY].PRICE.INCREASE_BY: {
      return bulkEdit({
        attribute: ATTRIBUTE.PRICE,
        updateInventory(productInventory) {
          const absolute = operation.getIn(['value', 'type']) === PRICE_TYPE.ABSOLUTE;
          const cents = operation.getIn(['value', 'rounding']);
          const operationValue = operation.getIn(['value', 'value']) || AMOUNT.ZERO_CENT;
          const productValue = productInventory.getIn([ATTRIBUTE.PRICE, VALUE]) || AMOUNT.ZERO_CENT;
          const value = getPriceValue({ absolute, cents, operationType, operationValue, productValue });

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.PRICE, value })
            ? ERROR
            : productInventory.setIn([ATTRIBUTE.PRICE, VALUE], value);
        },
        updateCombination(combination) {
          const absolute = operation.getIn(['value', 'type']) === PRICE_TYPE.ABSOLUTE;
          const cents = operation.getIn(['value', 'rounding']);
          const operationValue = operation.getIn(['value', 'value']) || AMOUNT.ZERO_CENT;
          const productValue = combination.get(ATTRIBUTE.PRICE) || AMOUNT.ZERO_CENT;
          const value = getPriceValue({ absolute, cents, operationType, operationValue, productValue });

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.PRICE, value })
            ? ERROR
            : combination.set(ATTRIBUTE.PRICE, value);
        },
      });
    }

    case OPERATIONS[EBAY].QUANTITY.CHANGE_TO:
    case OPERATIONS[EBAY].QUANTITY.DECREASE_BY:
    case OPERATIONS[EBAY].QUANTITY.INCREASE_BY: {
      return bulkEdit({
        attribute: ATTRIBUTE.QUANTITY,
        updateInventory(productInventory) {
          const operationValue = operation.get('value') || AMOUNT.ZERO;
          const productValue = productInventory.getIn([ATTRIBUTE.QUANTITY, VALUE]) || AMOUNT.ZERO;
          const value = getNumberValue({ decimals: 0, operationType, operationValue, productValue });

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.QUANTITY, value })
            ? ERROR
            : productInventory.setIn([ATTRIBUTE.QUANTITY, VALUE], value);
        },
        updateCombination(combination) {
          const operationValue = operation.get('value') || AMOUNT.ZERO;
          const productValue = combination.get(ATTRIBUTE.QUANTITY) || AMOUNT.ZERO;
          const value = getNumberValue({ decimals: 0, operationType, operationValue, productValue });

          if (value === productValue) return NO_CHANGES;

          return getError({ allowZero: true, channel, type: ATTRIBUTE.QUANTITY, value })
            ? ERROR
            : combination.set(ATTRIBUTE.QUANTITY, value);
        },
      });
    }

    case OPERATIONS[EBAY].SKU.ADD_AFTER: {
      return bulkEdit({
        attribute: ATTRIBUTE.SKU,
        updateInventory(productInventory) {
          const productValue = productInventory.getIn([ATTRIBUTE.SKU, VALUE]) || '';
          const operationValue = operation.get('value');
          const value = `${productValue}${operationValue}`;

          if (value === productValue) return NO_CHANGES;

          const allowEmpty = !productInventory.getIn([ATTRIBUTE.SKU, REQUIRED]);
          const error = getError({ allowEmpty, channel, type: ATTRIBUTE.SKU, value });

          return error
            ? ERROR
            : productInventory.setIn([ATTRIBUTE.SKU, VALUE], value);
        },
        updateCombination(combination) {
          const productValue = combination.get(ATTRIBUTE.SKU) || '';
          const operationValue = operation.get('value');
          const value = `${productValue}${operationValue}`;

          if (value === productValue) return NO_CHANGES;

          const allowEmpty = !source.getIn([FORM.VARIATIONS, REQUIRED, ATTRIBUTE.SKU]);
          const error = getError({ allowEmpty, channel, type: ATTRIBUTE.SKU, value });

          return error
            ? ERROR
            : combination.set(ATTRIBUTE.SKU, value);
        },
      });
    }

    case OPERATIONS[EBAY].SKU.ADD_BEFORE: {
      return bulkEdit({
        attribute: ATTRIBUTE.SKU,
        updateInventory(productInventory) {
          const productValue = productInventory.getIn([ATTRIBUTE.SKU, VALUE]) || '';
          const operationValue = operation.get('value');
          const value = `${operationValue}${productValue}`;

          if (value === productValue) return NO_CHANGES;

          const allowEmpty = !productInventory.getIn([ATTRIBUTE.SKU, REQUIRED]);
          const error = getError({ allowEmpty, channel, type: ATTRIBUTE.SKU, value });

          return error
            ? ERROR
            : productInventory.setIn([ATTRIBUTE.SKU, VALUE], value);
        },
        updateCombination(combination) {
          const productValue = combination.get(ATTRIBUTE.SKU) || '';
          const operationValue = operation.get('value');
          const value = `${operationValue}${productValue}`;

          if (value === productValue) return NO_CHANGES;

          const allowEmpty = !source.getIn([FORM.VARIATIONS, REQUIRED, ATTRIBUTE.SKU]);
          const error = getError({ allowEmpty, channel, type: ATTRIBUTE.SKU, value });

          return error
            ? ERROR
            : combination.set(ATTRIBUTE.SKU, value);
        },
      });
    }

    case OPERATIONS[EBAY].SKU.CHANGE_TO: {
      return bulkEdit({
        attribute: ATTRIBUTE.SKU,
        updateInventory(productInventory) {
          const productValue = productInventory.getIn([ATTRIBUTE.SKU, VALUE]) || '';
          const value = operation.get('value');

          if (value === productValue) return NO_CHANGES;

          const allowEmpty = !productInventory.getIn([ATTRIBUTE.SKU, REQUIRED]);
          const error = getError({ allowEmpty, channel, type: ATTRIBUTE.SKU, value });

          return error
            ? ERROR
            : productInventory.setIn([ATTRIBUTE.SKU, VALUE], value);
        },
        updateCombination(combination) {
          const productValue = combination.get(ATTRIBUTE.SKU) || '';
          const value = operation.get('value');

          if (value === productValue) return NO_CHANGES;

          const allowEmpty = !source.getIn([FORM.VARIATIONS, REQUIRED, ATTRIBUTE.SKU]);
          const error = getError({ allowEmpty, channel, type: ATTRIBUTE.SKU, value });

          return error
            ? ERROR
            : combination.set(ATTRIBUTE.SKU, value);
        },
      });
    }

    case OPERATIONS[EBAY].SKU.DELETE: {
      return bulkEdit({
        attribute: ATTRIBUTE.SKU,
        updateInventory(productInventory) {
          const productValue = productInventory.getIn([ATTRIBUTE.SKU, VALUE]);

          if (!productValue) return NO_CHANGES;

          const operationValue = operation.get('value');
          const pattern = new RegExp(escapeRegex(operationValue), 'ig');
          const value = productValue.replace(pattern, '');

          if (value === productValue) return NO_CHANGES;

          const allowEmpty = !productInventory.getIn([ATTRIBUTE.SKU, REQUIRED]);

          return getError({ allowEmpty, channel, type: ATTRIBUTE.SKU, value })
            ? ERROR
            : productInventory.setIn([ATTRIBUTE.SKU, VALUE], value);
        },
        updateCombination(combination) {
          const productValue = combination.get(ATTRIBUTE.SKU);

          if (!productValue) return NO_CHANGES;

          const operationValue = operation.get('value');
          const pattern = new RegExp(escapeRegex(operationValue), 'ig');
          const value = productValue.replace(pattern, '');

          if (value === productValue) return NO_CHANGES;

          const allowEmpty = !source.getIn([FORM.VARIATIONS, REQUIRED, ATTRIBUTE.SKU]);

          return getError({ allowEmpty, channel, type: ATTRIBUTE.SKU, value })
            ? ERROR
            : combination.set(ATTRIBUTE.SKU, value);
        },
      });
    }

    case OPERATIONS[EBAY].SKU.FIND_AND_REPLACE: {
      return bulkEdit({
        attribute: ATTRIBUTE.SKU,
        updateInventory(productInventory) {
          const productValue = productInventory.getIn([ATTRIBUTE.SKU, VALUE]);

          if (!productValue) return NO_CHANGES;

          const find = operation.getIn(['value', 'find']);
          const replace = operation.getIn(['value', 'replace']);
          const pattern = new RegExp(escapeRegex(find), 'ig');
          const value = productValue.replace(pattern, replace);

          if (value === productValue) return NO_CHANGES;

          const allowEmpty = !productInventory.getIn([ATTRIBUTE.SKU, REQUIRED]);

          return getError({ allowEmpty, channel, type: ATTRIBUTE.SKU, value })
            ? ERROR
            : productInventory.setIn([ATTRIBUTE.SKU, VALUE], value);
        },
        updateCombination(combination) {
          const productValue = combination.get(ATTRIBUTE.SKU);

          if (!productValue) return NO_CHANGES;

          const find = operation.getIn(['value', 'find']);
          const replace = operation.getIn(['value', 'replace']);
          const pattern = new RegExp(escapeRegex(find), 'ig');
          const value = productValue.replace(pattern, replace);

          if (value === productValue) return NO_CHANGES;

          const allowEmpty = !source.getIn([FORM.VARIATIONS, REQUIRED, ATTRIBUTE.SKU]);

          return getError({ allowEmpty, channel, type: ATTRIBUTE.SKU, value })
            ? ERROR
            : combination.set(ATTRIBUTE.SKU, value);
        },
      });
    }

    default: {
      return source;
    }
  }
}

function updateEtsyInventory({ operation, product: source }) {
  const channel = source.get('channel');
  const operationType = operation.get('type');

  function bulkEdit({ attribute, updateInventory = void0, updateCombination = void0 }) {
    let product = source;
    const size = getSize(product.getIn([FORM.VARIATIONS, COMBINATIONS]));

    function findVariantById(variantId) {
      return function find(variant) {
        return variant.get('id') === variantId;
      };
    }

    function findCombinationById(combinationId) {
      return function find(combination) {
        return combination.get('id') === combinationId;
      };
    }

    function getTotalQuantity(sum, item) {
      return item.get('visibility')
        ? sum + parseInt(item.get(ATTRIBUTE.QUANTITY) || 0)
        : sum;
    }

    for (let valueIndex = 0; valueIndex < getSize(operation.get('value')); valueIndex++) {
      const operationOfferingId = operation.getIn(['value', valueIndex, 'offeringId'], null);
      const operationCombination = operation.getIn(['value', valueIndex, 'combination'], null);

      if (operationCombination === null && operationOfferingId === null) {
        if (getSize(product.getIn([FORM.VARIATIONS, INFLUENCE, attribute])) === 0) {
          const productInventory = updateInventory(product.get(FORM.INVENTORY), valueIndex);

          if (productInventory === ERROR) return source;

          if (productInventory !== NO_CHANGES) {
            product = clearErrors({
              errors: [attribute],
              path: FORM.INVENTORY,
              product: product.set(FORM.INVENTORY, productInventory),
            });
          }
        }

        if (size) {
          for (let combinationIndex = 0; combinationIndex < size; combinationIndex++) {
            const combination = updateCombination(
              product.getIn([FORM.VARIATIONS, COMBINATIONS, combinationIndex]),
              valueIndex
            );

            if (combination === ERROR) return source;

            if (combination === NO_CHANGES) continue;

            product = clearVariationsCombinationErrors({
              errors: [attribute],
              combinationIndex,
              product: product.setIn([FORM.VARIATIONS, COMBINATIONS, combinationIndex], combination),
            });
          }
        }
      } else if (operationCombination !== null) {
        if (size) {
          const optionId = operationCombination.get('optionId');
          const variationId = operationCombination.get('variationId');
          const variantIndex = product.getIn([FORM.VARIATIONS, VARIANTS]).findIndex(findVariantById(variationId));

          if (variantIndex === -1) return source;

          for (let combinationIndex = 0; combinationIndex < size; combinationIndex++) {
            let combination = product.getIn([FORM.VARIATIONS, COMBINATIONS, combinationIndex]);

            if (combination.getIn(['options', variantIndex, 'optionId']) !== optionId) continue;

            combination = updateCombination(combination, valueIndex);

            if (combination === ERROR) return source;

            if (combination === NO_CHANGES) continue;

            product = clearVariationsCombinationErrors({
              errors: [attribute],
              combinationIndex,
              product: product.setIn([FORM.VARIATIONS, COMBINATIONS, combinationIndex], combination),
            });
          }
        }
      } else if (operationOfferingId !== null) {
        if (size) {
          const combinationIndex = product
            .getIn([FORM.VARIATIONS, COMBINATIONS])
            .findIndex(findCombinationById(operationOfferingId));

          if (combinationIndex === -1) return source;

          const combination = updateCombination(
            product.getIn([FORM.VARIATIONS, COMBINATIONS, combinationIndex]),
            valueIndex
          );

          if (combination === ERROR) return source;

          if (combination === NO_CHANGES) continue;

          product = clearVariationsCombinationErrors({
            errors: [attribute],
            combinationIndex,
            product: product.setIn([FORM.VARIATIONS, COMBINATIONS, combinationIndex], combination),
          });
        }
      }
    }

    if (
      size > 0 &&
      attribute === ATTRIBUTE.QUANTITY &&
      getSize(product.getIn([FORM.VARIATIONS, INFLUENCE, ATTRIBUTE.QUANTITY])) > 0 &&
      !product.getIn([FORM.VARIATIONS, COMBINATIONS]).reduce(getTotalQuantity, 0)
    ) {
      return source;
    }

    return product;
  }

  switch (operationType) {
    case OPERATIONS[ETSY].PRICE.CHANGE_TO:
    case OPERATIONS[ETSY].PRICE.DECREASE_BY:
    case OPERATIONS[ETSY].PRICE.INCREASE_BY: {
      return bulkEdit({
        attribute: ATTRIBUTE.PRICE,
        updateInventory(productInventory, valueIndex) {
          const absolute = operation.getIn(['value', valueIndex, 'value', 'type']) === PRICE_TYPE.ABSOLUTE;
          const cents = operation.getIn(['value', valueIndex, 'value', 'rounding']);
          const operationValue = operation.getIn(['value', valueIndex, 'value', 'value']) || AMOUNT.ZERO_CENT;
          const productValue = productInventory.getIn([ATTRIBUTE.PRICE, VALUE]) || AMOUNT.ZERO_CENT;
          const value = getPriceValue({ absolute, cents, operationType, operationValue, productValue });

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.PRICE, value })
            ? ERROR
            : productInventory.setIn([ATTRIBUTE.PRICE, VALUE], value);
        },
        updateCombination(combination, valueIndex) {
          const absolute = operation.getIn(['value', valueIndex, 'value', 'type']) === PRICE_TYPE.ABSOLUTE;
          const cents = operation.getIn(['value', valueIndex, 'value', 'rounding']);
          const operationValue = operation.getIn(['value', valueIndex, 'value', 'value']) || AMOUNT.ZERO_CENT;
          const productValue = combination.get(ATTRIBUTE.PRICE) || AMOUNT.ZERO_CENT;
          const value = getPriceValue({ absolute, cents, operationType, operationValue, productValue });

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.PRICE, value })
            ? ERROR
            : combination.set(ATTRIBUTE.PRICE, value);
        },
      });
    }

    case OPERATIONS[ETSY].QUANTITY.CHANGE_TO:
    case OPERATIONS[ETSY].QUANTITY.DECREASE_BY:
    case OPERATIONS[ETSY].QUANTITY.INCREASE_BY: {
      return bulkEdit({
        attribute: ATTRIBUTE.QUANTITY,
        updateInventory(productInventory, valueIndex) {
          const operationValue = operation.getIn(['value', valueIndex, 'value']) || AMOUNT.ZERO;
          const productValue = productInventory.getIn([ATTRIBUTE.QUANTITY, VALUE]) || AMOUNT.ZERO;
          const value = getNumberValue({ decimals: 0, operationType, operationValue, productValue });

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.QUANTITY, value })
            ? ERROR
            : productInventory.setIn([ATTRIBUTE.QUANTITY, VALUE], value);
        },
        updateCombination(combination, valueIndex) {
          const operationValue = operation.getIn(['value', valueIndex, 'value']) || AMOUNT.ZERO;
          const productValue = combination.get(ATTRIBUTE.QUANTITY) || AMOUNT.ZERO;
          const value = getNumberValue({ decimals: 0, operationType, operationValue, productValue });

          if (value === productValue) return NO_CHANGES;

          return getError({ allowZero: true, channel, type: ATTRIBUTE.QUANTITY, value })
            ? ERROR
            : combination.set(ATTRIBUTE.QUANTITY, value);
        },
      });
    }

    case OPERATIONS[ETSY].SKU.ADD_AFTER: {
      return bulkEdit({
        attribute: ATTRIBUTE.SKU,
        updateInventory(productInventory, valueIndex) {
          const productValue = productInventory.getIn([ATTRIBUTE.SKU, VALUE]) || '';
          const operationValue = operation.getIn(['value', valueIndex, 'value']);
          const value = `${productValue}${operationValue}`;

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.SKU, value })
            ? ERROR
            : productInventory.setIn([ATTRIBUTE.SKU, VALUE], value);
        },
        updateCombination(combination, valueIndex) {
          const productValue = combination.get(ATTRIBUTE.SKU) || '';
          const operationValue = operation.getIn(['value', valueIndex, 'value']);
          const value = `${productValue}${operationValue}`;

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.SKU, value })
            ? ERROR
            : combination.set(ATTRIBUTE.SKU, value);
        },
      });
    }

    case OPERATIONS[ETSY].SKU.ADD_BEFORE: {
      return bulkEdit({
        attribute: ATTRIBUTE.SKU,
        updateInventory(productInventory, valueIndex) {
          const productValue = productInventory.getIn([ATTRIBUTE.SKU, VALUE]) || '';
          const operationValue = operation.getIn(['value', valueIndex, 'value']);
          const value = `${operationValue}${productValue}`;

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.SKU, value })
            ? ERROR
            : productInventory.setIn([ATTRIBUTE.SKU, VALUE], value);
        },
        updateCombination(combination, valueIndex) {
          const productValue = combination.get(ATTRIBUTE.SKU) || '';
          const operationValue = operation.getIn(['value', valueIndex, 'value']);
          const value = `${operationValue}${productValue}`;

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.SKU, value })
            ? ERROR
            : combination.set(ATTRIBUTE.SKU, value);
        },
      });
    }

    case OPERATIONS[ETSY].SKU.CHANGE_TO: {
      return bulkEdit({
        attribute: ATTRIBUTE.SKU,
        updateInventory(productInventory, valueIndex) {
          const productValue = productInventory.getIn([ATTRIBUTE.SKU, VALUE]) || '';
          const value = operation.getIn(['value', valueIndex, 'value']);

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.SKU, value })
            ? ERROR
            : productInventory.setIn([ATTRIBUTE.SKU, VALUE], value);
        },
        updateCombination(combination, valueIndex) {
          const productValue = combination.get(ATTRIBUTE.SKU) || '';
          const value = operation.getIn(['value', valueIndex, 'value']);

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.SKU, value })
            ? ERROR
            : combination.set(ATTRIBUTE.SKU, value);
        },
      });
    }

    case OPERATIONS[ETSY].SKU.DELETE: {
      return bulkEdit({
        attribute: ATTRIBUTE.SKU,
        updateInventory(productInventory, valueIndex) {
          const productValue = productInventory.getIn([ATTRIBUTE.SKU, VALUE]);

          if (!productValue) return NO_CHANGES;

          const operationValue = operation.getIn(['value', valueIndex, 'value']);
          const pattern = new RegExp(escapeRegex(operationValue), 'ig');
          const value = productValue.replace(pattern, '');

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.SKU, value })
            ? ERROR
            : productInventory.setIn([ATTRIBUTE.SKU, VALUE], value);
        },
        updateCombination(combination, valueIndex) {
          const productValue = combination.get(ATTRIBUTE.SKU);

          if (!productValue) return NO_CHANGES;

          const operationValue = operation.getIn(['value', valueIndex, 'value']);
          const pattern = new RegExp(escapeRegex(operationValue), 'ig');
          const value = productValue.replace(pattern, '');

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.SKU, value })
            ? ERROR
            : combination.set(ATTRIBUTE.SKU, value);
        },
      });
    }

    case OPERATIONS[ETSY].SKU.FIND_AND_REPLACE: {
      return bulkEdit({
        attribute: ATTRIBUTE.SKU,
        updateInventory(productInventory, valueIndex) {
          const productValue = productInventory.getIn([ATTRIBUTE.SKU, VALUE]);

          if (!productValue) return NO_CHANGES;

          const find = operation.getIn(['value', valueIndex, 'value', 'find']);
          const replace = operation.getIn(['value', valueIndex, 'value', 'replace']);
          const pattern = new RegExp(escapeRegex(find), 'ig');
          const value = productValue.replace(pattern, replace);

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.SKU, value })
            ? ERROR
            : productInventory.setIn([ATTRIBUTE.SKU, VALUE], value);
        },
        updateCombination(combination, valueIndex) {
          const productValue = combination.get(ATTRIBUTE.SKU);

          if (!productValue) return NO_CHANGES;

          const find = operation.getIn(['value', valueIndex, 'value', 'find']);
          const replace = operation.getIn(['value', valueIndex, 'value', 'replace']);
          const pattern = new RegExp(escapeRegex(find), 'ig');
          const value = productValue.replace(pattern, replace);

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.SKU, value })
            ? ERROR
            : combination.set(ATTRIBUTE.SKU, value);
        },
      });
    }

    default: {
      return source;
    }
  }
}

function updateFaireInventory({ operation, product: source }) {
  const channel = source.get('channel');
  const operationType = operation.get('type');

  function bulkEdit({ attributes, updateCombination, updateInfluence, updateInventory }) {
    let product = source;
    const shouldClearErrors = Array.isArray(attributes);
    const size = getSize(product.getIn([FORM.VARIATIONS, COMBINATIONS]));

    if (typeof updateInfluence === 'function') {
      product = product.set(FORM.VARIATIONS, updateInfluence(product.get(FORM.VARIATIONS)));
    }

    if (typeof updateInventory === 'function') {
      const productInventory = updateInventory(product.get(FORM.INVENTORY));

      if (productInventory === ERROR) {
        return source;
      }

      if (productInventory !== NO_CHANGES) {
        if (shouldClearErrors) {
          product = clearErrors({
            errors: attributes,
            path: FORM.INVENTORY,
            product: product.set(FORM.INVENTORY, productInventory),
          });
        } else {
          product = product.set(FORM.INVENTORY, productInventory);
        }
      }
    }

    if (size > 0) {
      if (typeof updateCombination === 'function') {
        for (let combinationIndex = 0; combinationIndex < size; combinationIndex++) {
          const combination = updateCombination(product.getIn([FORM.VARIATIONS, COMBINATIONS, combinationIndex]));

          if (combination === ERROR) {
            return source;
          }

          if (combination === NO_CHANGES) continue;

          if (shouldClearErrors) {
            product = clearVariationsCombinationErrors({
              errors: attributes,
              combinationIndex,
              product: product.setIn([FORM.VARIATIONS, COMBINATIONS, combinationIndex], combination),
            });
          } else {
            product = product.setIn([FORM.VARIATIONS, COMBINATIONS, combinationIndex], combination);
          }
        }
        if (shouldClearErrors) {
          return product
            .getIn([FORM.VARIATIONS, ERROR])
            .reduce(updateProductErrorsFromVariations, product);
        }
      }
    }

    return product;
  }

  switch (operationType) {
    case OPERATIONS[FAIRE].CONTINUE_SELLING.SET: {
      return bulkEdit({
        updateInventory(productInventory) {
          const value = operation.get('value');
          return productInventory.setIn([ATTRIBUTE.CONTINUE_SELLING, VALUE], value);
        },
        updateInfluence(variations) {
          const value = operation.get('value');
          return variations.setIn([INFLUENCE, ATTRIBUTE.CONTINUE_SELLING], value);
        },
      });
    }

    case OPERATIONS[FAIRE].DEADLINE_DATE.SET: {
      return bulkEdit({
        attributes: [ATTRIBUTE.DEADLINE_DATE],
        updateInventory(productInventory) {
          if (
            productInventory.getIn([ATTRIBUTE.PREORDER, OVERSET]) ||
            !productInventory.getIn([ATTRIBUTE.PREORDER, VALUE]) ||
            productInventory.getIn([ATTRIBUTE.KEEP_ACTIVE, VALUE])
          ) {
            return NO_CHANGES;
          }

          const operationValue = shapeKeyDate(operation.get('value'));
          const productValue = productInventory.getIn([ATTRIBUTE.DEADLINE_DATE, VALUE]);

          if (operationValue === productValue) return NO_CHANGES;

          return productInventory.setIn([ATTRIBUTE.DEADLINE_DATE, VALUE], operationValue);
        },
        updateCombination(combination) {
          if (
            !combination.getIn([ATTRIBUTE.PREORDER, VALUE]) ||
            source.getIn([FORM.VARIATIONS, INFLUENCE, ATTRIBUTE.KEEP_ACTIVE])
          ) {
            return NO_CHANGES;
          }

          const operationValue = shapeKeyDate(operation.get('value'));
          const productValue = combination.get(ATTRIBUTE.DEADLINE_DATE);

          if (operationValue === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.DEADLINE_DATE, value: operationValue })
            ? ERROR
            : combination.set(ATTRIBUTE.DEADLINE_DATE, operationValue);
        },
      });
    }

    case OPERATIONS[FAIRE].DISTANCE_UNIT: {
      return bulkEdit({
        updateInventory(productInventory) {
          const productValue = productInventory.getIn([ATTRIBUTE.DISTANCE_UNIT, VALUE]);
          const operationValue = operation.get('value');
          const value = shapeFaireDistanceUnitForApp(operationValue);

          if (value === productValue) return NO_CHANGES;

          return productInventory.setIn([ATTRIBUTE.DISTANCE_UNIT, VALUE], value);
        },
        updateCombination(combination) {
          const operationValue = operation.get('value');
          const productValue = combination.get(ATTRIBUTE.DISTANCE_UNIT);
          const value = shapeFaireDistanceUnitForApp(operationValue);

          if (value === productValue) return NO_CHANGES;

          return combination.set(ATTRIBUTE.DISTANCE_UNIT, value);
        },
      });
    }

    case OPERATIONS[FAIRE].END_DATE.SET: {
      return bulkEdit({
        attributes: [ATTRIBUTE.END_DATE],
        updateInventory(productInventory) {
          if (
            productInventory.getIn([ATTRIBUTE.PREORDER, OVERSET]) ||
            !productInventory.getIn([ATTRIBUTE.PREORDER, VALUE])
          ) {
            return NO_CHANGES;
          }

          const operationValue = shapeKeyDate(operation.get('value'));
          const productValue = productInventory.getIn([ATTRIBUTE.END_DATE, VALUE]);

          if (operationValue === productValue) return NO_CHANGES;

          return productInventory.setIn([ATTRIBUTE.END_DATE, VALUE], operationValue);
        },
        updateCombination(combination) {
          if (!combination.getIn([ATTRIBUTE.PREORDER, VALUE])) {
            return NO_CHANGES;
          }

          const operationValue = shapeKeyDate(operation.get('value'));
          const productValue = combination.get(ATTRIBUTE.END_DATE);

          if (operationValue === productValue) return NO_CHANGES;

          return combination.set(ATTRIBUTE.END_DATE, operationValue);
        },
      });
    }

    case OPERATIONS[FAIRE].GTIN.ADD_AFTER: {
      return bulkEdit({
        attributes: [ATTRIBUTE.GTIN],
        updateInventory(productInventory) {
          if (productInventory.getIn([ATTRIBUTE.GTIN, OVERSET])) return NO_CHANGES;

          const productValue = productInventory.getIn([ATTRIBUTE.GTIN, VALUE]) || '';
          const operationValue = operation.get('value');
          const value = `${productValue}${operationValue}`;

          if (value === productValue) return NO_CHANGES;

          const error = getError({ channel, type: ATTRIBUTE.GTIN, value });

          return error
            ? ERROR
            : productInventory.setIn([ATTRIBUTE.GTIN, VALUE], value);
        },
        updateCombination(combination) {
          const productValue = combination.get(ATTRIBUTE.GTIN) || '';
          const operationValue = operation.get('value');
          const value = `${productValue}${operationValue}`;

          if (value === productValue) return NO_CHANGES;

          const error = getError({ channel, type: ATTRIBUTE.GTIN, value });

          return error
            ? ERROR
            : combination.set(ATTRIBUTE.GTIN, value);
        },
      });
    }

    case OPERATIONS[FAIRE].GTIN.ADD_BEFORE: {
      return bulkEdit({
        attributes: [ATTRIBUTE.GTIN],
        updateInventory(productInventory) {
          if (productInventory.getIn([ATTRIBUTE.GTIN, OVERSET])) return NO_CHANGES;

          const productValue = productInventory.getIn([ATTRIBUTE.GTIN, VALUE]) || '';
          const operationValue = operation.get('value');
          const value = `${operationValue}${productValue}`;

          if (value === productValue) return NO_CHANGES;

          const error = getError({ channel, type: ATTRIBUTE.GTIN, value });

          return error
            ? ERROR
            : productInventory.setIn([ATTRIBUTE.GTIN, VALUE], value);
        },
        updateCombination(combination) {
          const productValue = combination.get(ATTRIBUTE.GTIN) || '';
          const operationValue = operation.get('value');
          const value = `${operationValue}${productValue}`;

          if (value === productValue) return NO_CHANGES;

          const error = getError({ channel, type: ATTRIBUTE.GTIN, value });

          return error
            ? ERROR
            : combination.set(ATTRIBUTE.GTIN, value);
        },
      });
    }

    case OPERATIONS[FAIRE].GTIN.CHANGE_TO: {
      return bulkEdit({
        attributes: [ATTRIBUTE.GTIN],
        updateInventory(productInventory) {
          if (productInventory.getIn([ATTRIBUTE.GTIN, OVERSET])) return NO_CHANGES;

          const productValue = productInventory.getIn([ATTRIBUTE.GTIN, VALUE]) || '';
          const value = operation.get('value');

          if (value === productValue) return NO_CHANGES;

          const error = getError({ channel, type: ATTRIBUTE.GTIN, value });

          return error
            ? ERROR
            : productInventory.setIn([ATTRIBUTE.GTIN, VALUE], value);
        },
        updateCombination(combination) {
          const productValue = combination.get(ATTRIBUTE.GTIN) || '';
          const value = operation.get('value');

          if (value === productValue) return NO_CHANGES;

          const error = getError({ channel, type: ATTRIBUTE.GTIN, value });

          return error
            ? ERROR
            : combination.set(ATTRIBUTE.GTIN, value);
        },
      });
    }

    case OPERATIONS[FAIRE].GTIN.DELETE: {
      return bulkEdit({
        attributes: [ATTRIBUTE.GTIN],
        updateInventory(productInventory) {
          if (productInventory.getIn([ATTRIBUTE.GTIN, OVERSET])) return NO_CHANGES;

          const productValue = productInventory.getIn([ATTRIBUTE.GTIN, VALUE]);

          if (!productValue) return NO_CHANGES;

          const operationValue = operation.get('value');
          const pattern = new RegExp(escapeRegex(operationValue), 'ig');
          const value = productValue.replace(pattern, '');

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.GTIN, value })
            ? ERROR
            : productInventory.setIn([ATTRIBUTE.GTIN, VALUE], value);
        },
        updateCombination(combination) {
          const productValue = combination.get(ATTRIBUTE.GTIN);

          if (!productValue) return NO_CHANGES;

          const operationValue = operation.get('value');
          const pattern = new RegExp(escapeRegex(operationValue), 'ig');
          const value = productValue.replace(pattern, '');

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.GTIN, value })
            ? ERROR
            : combination.set(ATTRIBUTE.GTIN, value);
        },
      });
    }

    case OPERATIONS[FAIRE].GTIN.FIND_AND_REPLACE: {
      return bulkEdit({
        attributes: [ATTRIBUTE.GTIN],
        updateInventory(productInventory) {
          if (productInventory.getIn([ATTRIBUTE.GTIN, OVERSET])) return NO_CHANGES;

          const productValue = productInventory.getIn([ATTRIBUTE.GTIN, VALUE]);

          if (!productValue) return NO_CHANGES;

          const find = operation.getIn(['value', 'find']);
          const replace = operation.getIn(['value', 'replace']);
          const pattern = new RegExp(escapeRegex(find), 'ig');
          const value = productValue.replace(pattern, replace);

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.GTIN, value })
            ? ERROR
            : productInventory.setIn([ATTRIBUTE.GTIN, VALUE], value);
        },
        updateCombination(combination) {
          const productValue = combination.get(ATTRIBUTE.GTIN);

          if (!productValue) return NO_CHANGES;

          const find = operation.getIn(['value', 'find']);
          const replace = operation.getIn(['value', 'replace']);
          const pattern = new RegExp(escapeRegex(find), 'ig');
          const value = productValue.replace(pattern, replace);

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.GTIN, value })
            ? ERROR
            : combination.set(ATTRIBUTE.GTIN, value);
        },
      });
    }

    case OPERATIONS[FAIRE].HEIGHT.DECREASE_BY:
    case OPERATIONS[FAIRE].HEIGHT.INCREASE_BY:
    case OPERATIONS[FAIRE].HEIGHT.SET: {
      return bulkEdit({
        attributes: [ATTRIBUTE.HEIGHT],
        updateInventory(productInventory) {
          if (productInventory.getIn([ATTRIBUTE.HEIGHT, OVERSET])) return NO_CHANGES;

          const operationValue = operation.get('value') || AMOUNT.ZERO;
          const productValue = productInventory.getIn([ATTRIBUTE.HEIGHT, VALUE]) || AMOUNT.ZERO;
          const value = getNumberValue({ operationType, operationValue, productValue });

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.HEIGHT, value })
            ? ERROR
            : productInventory.setIn([ATTRIBUTE.HEIGHT, VALUE], value);
        },
        updateCombination(combination) {
          const operationValue = operation.get('value') || AMOUNT.ZERO;
          const productValue = combination.get(ATTRIBUTE.HEIGHT) || AMOUNT.ZERO;
          const value = getNumberValue({ operationType, operationValue, productValue });

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.HEIGHT, value })
            ? ERROR
            : combination.set(ATTRIBUTE.HEIGHT, value);
        },
      });
    }

    case OPERATIONS[FAIRE].KEEP_ACTIVE.SET: {
      return bulkEdit({
        updateInventory(productInventory) {
          const value = operation.get('value');
          return productInventory.setIn([ATTRIBUTE.KEEP_ACTIVE, VALUE], value);
        },
        updateInfluence(variations) {
          const value = operation.get('value');
          return variations.setIn([INFLUENCE, ATTRIBUTE.KEEP_ACTIVE], value);
        },
      });
    }

    case OPERATIONS[FAIRE].LENGTH.DECREASE_BY:
    case OPERATIONS[FAIRE].LENGTH.INCREASE_BY:
    case OPERATIONS[FAIRE].LENGTH.SET: {
      return bulkEdit({
        attributes: [ATTRIBUTE.LENGTH],
        updateInventory(productInventory) {
          if (productInventory.getIn([ATTRIBUTE.LENGTH, OVERSET])) return NO_CHANGES;

          const operationValue = operation.get('value') || AMOUNT.ZERO;
          const productValue = productInventory.getIn([ATTRIBUTE.LENGTH, VALUE]) || AMOUNT.ZERO;
          const value = getNumberValue({ operationType, operationValue, productValue });

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.LENGTH, value })
            ? ERROR
            : productInventory.setIn([ATTRIBUTE.LENGTH, VALUE], value);
        },
        updateCombination(combination) {
          const operationValue = operation.get('value') || AMOUNT.ZERO;
          const productValue = combination.get(ATTRIBUTE.LENGTH) || AMOUNT.ZERO;
          const value = getNumberValue({ operationType, operationValue, productValue });

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.LENGTH, value })
            ? ERROR
            : combination.set(ATTRIBUTE.LENGTH, value);
        },
      });
    }

    case OPERATIONS[FAIRE].MASS_UNIT: {
      return bulkEdit({
        updateInventory(productInventory) {
          const productValue = productInventory.getIn([ATTRIBUTE.MASS_UNIT, VALUE]);
          const operationValue = operation.get('value');
          const value = shapeFaireMassUnitForApp(operationValue);

          if (value === productValue) return NO_CHANGES;

          return productInventory.setIn([ATTRIBUTE.MASS_UNIT, VALUE], value);
        },
        updateCombination(combination) {
          const operationValue = operation.get('value');
          const productValue = combination.get(ATTRIBUTE.MASS_UNIT);
          const value = shapeFaireMassUnitForApp(operationValue);

          if (value === productValue) return NO_CHANGES;

          return combination.set(ATTRIBUTE.MASS_UNIT, value);
        },
      });
    }

    case OPERATIONS[FAIRE].PREORDER.SET: {
      return bulkEdit({
        updateInventory(productInventory) {
          if (productInventory.getIn([ATTRIBUTE.PREORDER, OVERSET])) return NO_CHANGES;

          const productValue = productInventory.getIn([ATTRIBUTE.PREORDER, VALUE]);
          const operationValue = operation.get('value');

          if (operationValue === productValue) return NO_CHANGES;

          return productInventory.setIn([ATTRIBUTE.PREORDER, VALUE], operationValue);
        },
        updateCombination(combination) {
          const operationValue = operation.get('value');
          const productValue = combination.get(ATTRIBUTE.PREORDER);

          if (operationValue === productValue) return NO_CHANGES;

          return combination.set(ATTRIBUTE.PREORDER, operationValue);
        },
      });
    }

    case OPERATIONS[FAIRE].QUANTITY.CHANGE_TO:
    case OPERATIONS[FAIRE].QUANTITY.DECREASE_BY:
    case OPERATIONS[FAIRE].QUANTITY.INCREASE_BY: {
      return bulkEdit({
        attributes: [ATTRIBUTE.QUANTITY],
        updateInventory(productInventory) {
          if (productInventory.getIn([ATTRIBUTE.QUANTITY, OVERSET])) return NO_CHANGES;

          const operationValue = operation.get('value') || AMOUNT.ZERO;
          const productValue = productInventory.getIn([ATTRIBUTE.QUANTITY, VALUE]) || AMOUNT.ZERO;
          const value = getNumberValue({ decimals: 0, operationType, operationValue, productValue });

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.QUANTITY, value })
            ? ERROR
            : productInventory.setIn([ATTRIBUTE.QUANTITY, VALUE], value);
        },
        updateCombination(combination) {
          const operationValue = operation.get('value') || AMOUNT.ZERO;
          const productValue = combination.get(ATTRIBUTE.QUANTITY) || AMOUNT.ZERO;
          const value = getNumberValue({ decimals: 0, operationType, operationValue, productValue });

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.QUANTITY, value })
            ? ERROR
            : combination.set(ATTRIBUTE.QUANTITY, value);
        },
      });
    }

    case OPERATIONS[FAIRE].RESTOCK_DATE.SET: {
      return bulkEdit({
        attributes: [ATTRIBUTE.RESTOCK_DATE],
        updateInventory(productInventory) {
          const operationValue = operation.get('value');
          const value = operationValue ? new Date(operationValue) : null;
          const productValue = productInventory.getIn([ATTRIBUTE.RESTOCK_DATE, VALUE]);
          const quantityValue = productInventory.getIn([ATTRIBUTE.QUANTITY, VALUE]);

          if (value === productValue || (value !== null && quantityValue !== AMOUNT.ZERO)) return NO_CHANGES;

          return productInventory.setIn([ATTRIBUTE.RESTOCK_DATE, VALUE], value);
        },
        updateCombination(combination) {
          const operationValue = operation.get('value');
          const value = operationValue ? new Date(operationValue) : null;
          const productValue = combination.get(ATTRIBUTE.RESTOCK_DATE);
          const quantityValue = combination.get(ATTRIBUTE.QUANTITY);

          if (value === productValue || (value !== null && quantityValue !== AMOUNT.ZERO)) return NO_CHANGES;

          return combination.set(ATTRIBUTE.RESTOCK_DATE, value);
        },
      });
    }

    case OPERATIONS[FAIRE].PRICES.DECREASE_BY:
    case OPERATIONS[FAIRE].PRICES.INCREASE_BY:
    case OPERATIONS[FAIRE].PRICES.SET: {
      const allowZero = !source.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, REQUIRED]);

      return bulkEdit({
        attributes: [ATTRIBUTE.RETAIL_PRICE, ATTRIBUTE.WHOLESALE_PRICE],
        updateInventory(sourceInventory) {
          if (sourceInventory.getIn([ATTRIBUTE.RETAIL_PRICE, OVERSET])) return NO_CHANGES;

          let productInventory = sourceInventory;

          for (const [key, values] of operation.get('value').entries()) {
            if (!PRICE_API_KEY_TO_ATTRIBUTE_CURRENCY_MAP.hasOwnProperty(key)) continue;

            const { attribute, currency } = PRICE_API_KEY_TO_ATTRIBUTE_CURRENCY_MAP[key];
            const operationValue = values.get('value');

            if (operationValue) {
              const absolute = values.get('type') !== PRICE_TYPE.PERCENTAGE;
              const cents = values.get('rounding');
              const productValue = productInventory.getIn([attribute, VALUE, currency]) || AMOUNT.ZERO_CENT;
              const value = getPriceValue({ absolute, cents, operationType, operationValue, productValue });

              if (value === productValue) continue;

              productInventory = productInventory.setIn([attribute, VALUE, currency], value);

              if (currency !== CURRENCY.USD && !productInventory.getIn([ATTRIBUTE.INTERNATIONAL_PRICING, VALUE])) {
                productInventory = productInventory.setIn([ATTRIBUTE.INTERNATIONAL_PRICING, VALUE], true);
              }
            } else {
              productInventory = productInventory.setIn([attribute, VALUE, currency], '');
            }
          }

          const updatedProduct = setRetailPriceErrors({
            product: setWholesalePriceErrors({
              product: source.set(FORM.INVENTORY, productInventory),
            }),
          });

          const retailErrors = updatedProduct.getIn([FORM.INVENTORY, ATTRIBUTE.RETAIL_PRICE, ERROR]);
          const wholesaleErrors = updatedProduct.getIn([FORM.INVENTORY, ATTRIBUTE.WHOLESALE_PRICE, ERROR]);

          if (retailErrors || wholesaleErrors) {
            for (const currency of CURRENCIES[FAIRE]) {
              if (retailErrors?.get(currency) || wholesaleErrors?.get(currency)) {
                productInventory = productInventory
                  .setIn([ATTRIBUTE.RETAIL_PRICE, VALUE, currency], sourceInventory.getIn([ATTRIBUTE.RETAIL_PRICE, VALUE, currency]))
                  .setIn([ATTRIBUTE.WHOLESALE_PRICE, VALUE, currency], sourceInventory.getIn([ATTRIBUTE.WHOLESALE_PRICE, VALUE, currency]));
              }
            }
          }

          return productInventory === sourceInventory ? NO_CHANGES : productInventory;
        },
        updateInfluence(variations) {
          for (const [key, values] of operation.get('value').entries()) {
            if (!PRICE_API_KEY_TO_ATTRIBUTE_CURRENCY_MAP.hasOwnProperty(key)) continue;

            const { currency } = PRICE_API_KEY_TO_ATTRIBUTE_CURRENCY_MAP[key];

            if (currency !== CURRENCY.USD && values.get('value')) {
              return variations.setIn([INFLUENCE, ATTRIBUTE.INTERNATIONAL_PRICING], true);
            }
          }

          return variations;
        },
        updateCombination(combination) {
          let updatedCombination = combination;

          for (const [key, values] of operation.get('value').entries()) {
            if (!PRICE_API_KEY_TO_ATTRIBUTE_CURRENCY_MAP.hasOwnProperty(key)) continue;

            const { attribute, currency } = PRICE_API_KEY_TO_ATTRIBUTE_CURRENCY_MAP[key];
            const operationValue = values.get('value');

            if (operationValue) {
              const absolute = values.get('type') !== PRICE_TYPE.PERCENTAGE;
              const cents = values.get('rounding');
              const productValue = updatedCombination.getIn([attribute, currency]) || AMOUNT.ZERO_CENT;
              const value = getPriceValue({ absolute, cents, operationType, operationValue, productValue });

              if (value === productValue) continue;

              updatedCombination = updatedCombination.setIn([attribute, currency], value);
            } else {
              updatedCombination = updatedCombination.setIn([attribute, VALUE, currency], '');
            }
          }

          for (const currency of CURRENCIES[FAIRE]) {
            const retail = updatedCombination.getIn([ATTRIBUTE.RETAIL_PRICE, currency]);
            const wholesale = updatedCombination.getIn([ATTRIBUTE.WHOLESALE_PRICE, currency]);

            if (
              getError({ allowZero, channel, currency, retail, wholesale, type: ATTRIBUTE.RETAIL_PRICE }) ||
              getError({ allowZero, channel, currency, retail, wholesale, type: ATTRIBUTE.WHOLESALE_PRICE })
            ) {
              updatedCombination = updatedCombination
                .setIn([ATTRIBUTE.RETAIL_PRICE, currency], combination.getIn([ATTRIBUTE.RETAIL_PRICE, currency]))
                .setIn([ATTRIBUTE.WHOLESALE_PRICE, currency], combination.getIn([ATTRIBUTE.WHOLESALE_PRICE, currency]));
            }
          }

          return updatedCombination === combination ? NO_CHANGES : updatedCombination;
        },
      });
    }

    case OPERATIONS[FAIRE].SKU.ADD_AFTER: {
      return bulkEdit({
        attributes: [ATTRIBUTE.SKU],
        updateInventory(productInventory) {
          if (productInventory.getIn([ATTRIBUTE.SKU, OVERSET])) return NO_CHANGES;

          const productValue = productInventory.getIn([ATTRIBUTE.SKU, VALUE]) || '';
          const operationValue = operation.get('value');
          const value = `${productValue}${operationValue}`;

          if (value === productValue) return NO_CHANGES;

          const error = getError({ channel, type: ATTRIBUTE.SKU, value });

          return error
            ? ERROR
            : productInventory.setIn([ATTRIBUTE.SKU, VALUE], value);
        },
        updateCombination(combination) {
          const productValue = combination.get(ATTRIBUTE.SKU) || '';
          const operationValue = operation.get('value');
          const value = `${productValue}${operationValue}`;

          if (value === productValue) return NO_CHANGES;

          const error = getError({ channel, type: ATTRIBUTE.SKU, value });

          return error
            ? ERROR
            : combination.set(ATTRIBUTE.SKU, value);
        },
      });
    }

    case OPERATIONS[FAIRE].SKU.ADD_BEFORE: {
      return bulkEdit({
        attributes: [ATTRIBUTE.SKU],
        updateInventory(productInventory) {
          if (productInventory.getIn([ATTRIBUTE.SKU, OVERSET])) return NO_CHANGES;

          const productValue = productInventory.getIn([ATTRIBUTE.SKU, VALUE]) || '';
          const operationValue = operation.get('value');
          const value = `${operationValue}${productValue}`;

          if (value === productValue) return NO_CHANGES;

          const error = getError({ channel, type: ATTRIBUTE.SKU, value });

          return error
            ? ERROR
            : productInventory.setIn([ATTRIBUTE.SKU, VALUE], value);
        },
        updateCombination(combination) {
          const productValue = combination.get(ATTRIBUTE.SKU) || '';
          const operationValue = operation.get('value');
          const value = `${operationValue}${productValue}`;

          if (value === productValue) return NO_CHANGES;

          const error = getError({ channel, type: ATTRIBUTE.SKU, value });

          return error
            ? ERROR
            : combination.set(ATTRIBUTE.SKU, value);
        },
      });
    }

    case OPERATIONS[FAIRE].SKU.CHANGE_TO: {
      return bulkEdit({
        attributes: [ATTRIBUTE.SKU],
        updateInventory(productInventory) {
          if (productInventory.getIn([ATTRIBUTE.SKU, OVERSET])) return NO_CHANGES;

          const productValue = productInventory.getIn([ATTRIBUTE.SKU, VALUE]) || '';
          const value = operation.get('value');

          if (value === productValue) return NO_CHANGES;

          const error = getError({ channel, type: ATTRIBUTE.SKU, value });

          return error
            ? ERROR
            : productInventory.setIn([ATTRIBUTE.SKU, VALUE], value);
        },
        updateCombination(combination) {
          const productValue = combination.get(ATTRIBUTE.SKU) || '';
          const value = operation.get('value');

          if (value === productValue) return NO_CHANGES;

          const error = getError({ channel, type: ATTRIBUTE.SKU, value });

          return error
            ? ERROR
            : combination.set(ATTRIBUTE.SKU, value);
        },
      });
    }

    case OPERATIONS[FAIRE].SKU.DELETE: {
      return bulkEdit({
        attributes: [ATTRIBUTE.SKU],
        updateInventory(productInventory) {
          if (productInventory.getIn([ATTRIBUTE.SKU, OVERSET])) return NO_CHANGES;

          const productValue = productInventory.getIn([ATTRIBUTE.SKU, VALUE]);

          if (!productValue) return NO_CHANGES;

          const operationValue = operation.get('value');
          const pattern = new RegExp(escapeRegex(operationValue), 'ig');
          const value = productValue.replace(pattern, '');

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.SKU, value })
            ? ERROR
            : productInventory.setIn([ATTRIBUTE.SKU, VALUE], value);
        },
        updateCombination(combination) {
          const productValue = combination.get(ATTRIBUTE.SKU);

          if (!productValue) return NO_CHANGES;

          const operationValue = operation.get('value');
          const pattern = new RegExp(escapeRegex(operationValue), 'ig');
          const value = productValue.replace(pattern, '');

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.SKU, value })
            ? ERROR
            : combination.set(ATTRIBUTE.SKU, value);
        },
      });
    }

    case OPERATIONS[FAIRE].SKU.FIND_AND_REPLACE: {
      return bulkEdit({
        attributes: [ATTRIBUTE.SKU],
        updateInventory(productInventory) {
          if (productInventory.getIn([ATTRIBUTE.SKU, OVERSET])) return NO_CHANGES;

          const productValue = productInventory.getIn([ATTRIBUTE.SKU, VALUE]);

          if (!productValue) return NO_CHANGES;

          const find = operation.getIn(['value', 'find']);
          const replace = operation.getIn(['value', 'replace']);
          const pattern = new RegExp(escapeRegex(find), 'ig');
          const value = productValue.replace(pattern, replace);

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.SKU, value })
            ? ERROR
            : productInventory.setIn([ATTRIBUTE.SKU, VALUE], value);
        },
        updateCombination(combination) {
          const productValue = combination.get(ATTRIBUTE.SKU);

          if (!productValue) return NO_CHANGES;

          const find = operation.getIn(['value', 'find']);
          const replace = operation.getIn(['value', 'replace']);
          const pattern = new RegExp(escapeRegex(find), 'ig');
          const value = productValue.replace(pattern, replace);

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.SKU, value })
            ? ERROR
            : combination.set(ATTRIBUTE.SKU, value);
        },
      });
    }

    case OPERATIONS[FAIRE].START_DATE.SET: {
      return bulkEdit({
        attributes: [ATTRIBUTE.END_DATE, ATTRIBUTE.DEADLINE_DATE, ATTRIBUTE.START_DATE],
        updateInventory(productInventory) {
          if (
            productInventory.getIn([ATTRIBUTE.PREORDER, OVERSET]) ||
            !productInventory.getIn([ATTRIBUTE.PREORDER, VALUE])
          ) {
            return NO_CHANGES;
          }

          const operationValue = shapeKeyDate(operation.get('value'));
          const productValue = productInventory.getIn([ATTRIBUTE.START_DATE, VALUE]);

          if (operationValue === productValue) return NO_CHANGES;

          return productInventory.setIn([ATTRIBUTE.START_DATE, VALUE], operationValue);
        },
        updateCombination(combination) {
          if (!combination.getIn([ATTRIBUTE.PREORDER, VALUE])) {
            return NO_CHANGES;
          }

          const operationValue = shapeKeyDate(operation.get('value'));
          const productValue = combination.get(ATTRIBUTE.START_DATE);

          if (operationValue === productValue) return NO_CHANGES;

          return combination.set(ATTRIBUTE.START_DATE, operationValue);
        },
      });
    }

    case OPERATIONS[FAIRE].TARIFF_CODE.SET: {
      return bulkEdit({
        attributes: [ATTRIBUTE.TARIFF_CODE],
        updateInventory(productInventory) {
          if (productInventory.getIn([ATTRIBUTE.TARIFF_CODE, OVERSET])) return NO_CHANGES;

          const productValue = productInventory.getIn([ATTRIBUTE.TARIFF_CODE, VALUE]) || '';
          const value = operation.get('value');

          if (value === productValue) return NO_CHANGES;

          const error = getError({ channel, type: ATTRIBUTE.TARIFF_CODE, value });

          return error
            ? ERROR
            : productInventory.setIn([ATTRIBUTE.TARIFF_CODE, VALUE], value);
        },
        updateCombination(combination) {
          const productValue = combination.get(ATTRIBUTE.TARIFF_CODE) || '';
          const value = operation.get('value');

          if (value === productValue) return NO_CHANGES;

          const error = getError({ channel, type: ATTRIBUTE.TARIFF_CODE, value });

          return error
            ? ERROR
            : combination.set(ATTRIBUTE.TARIFF_CODE, value);
        },
      });
    }

    case OPERATIONS[FAIRE].WEIGHT.DECREASE_BY:
    case OPERATIONS[FAIRE].WEIGHT.INCREASE_BY:
    case OPERATIONS[FAIRE].WEIGHT.SET: {
      return bulkEdit({
        attributes: [ATTRIBUTE.WEIGHT],
        updateInventory(productInventory) {
          if (productInventory.getIn([ATTRIBUTE.WEIGHT, OVERSET])) return NO_CHANGES;

          const operationValue = operation.get('value') || AMOUNT.ZERO;
          const productValue = productInventory.getIn([ATTRIBUTE.WEIGHT, VALUE]) || AMOUNT.ZERO;
          const value = getNumberValue({ operationType, operationValue, productValue });

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.WEIGHT, value })
            ? ERROR
            : productInventory.setIn([ATTRIBUTE.WEIGHT, VALUE], value);
        },
        updateCombination(combination) {
          const operationValue = operation.get('value') || AMOUNT.ZERO;
          const productValue = combination.get(ATTRIBUTE.WEIGHT) || AMOUNT.ZERO;
          const value = getNumberValue({ operationType, operationValue, productValue });

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.WEIGHT, value })
            ? ERROR
            : combination.set(ATTRIBUTE.WEIGHT, value);
        },
      });
    }

    case OPERATIONS[FAIRE].WIDTH.DECREASE_BY:
    case OPERATIONS[FAIRE].WIDTH.INCREASE_BY:
    case OPERATIONS[FAIRE].WIDTH.SET: {
      return bulkEdit({
        attributes: [ATTRIBUTE.WIDTH],
        updateInventory(productInventory) {
          if (productInventory.getIn([ATTRIBUTE.WIDTH, OVERSET])) return NO_CHANGES;

          const operationValue = operation.get('value') || AMOUNT.ZERO;
          const productValue = productInventory.getIn([ATTRIBUTE.WIDTH, VALUE]) || AMOUNT.ZERO;
          const value = getNumberValue({ operationType, operationValue, productValue });

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.WIDTH, value })
            ? ERROR
            : productInventory.setIn([ATTRIBUTE.WIDTH, VALUE], value);
        },
        updateCombination(combination) {
          const operationValue = operation.get('value') || AMOUNT.ZERO;
          const productValue = combination.get(ATTRIBUTE.WIDTH) || AMOUNT.ZERO;
          const value = getNumberValue({ operationType, operationValue, productValue });

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.WIDTH, value })
            ? ERROR
            : combination.set(ATTRIBUTE.WIDTH, value);
        },
      });
    }

    default: {
      return source;
    }
  }
}

function updateShopifyInventory({ operation, product: source }) {
  const channel = source.get('channel');
  const operationType = operation.get('type');

  function bulkEdit({ attributes, updateInventory = void0, updateCombination = void0 }) {
    let product = source;
    const shouldClearErrors = Array.isArray(attributes);
    const size = getSize(product.getIn([FORM.VARIATIONS, COMBINATIONS]));

    for (let valueIndex = 0; valueIndex < getSize(operation.get('value')); valueIndex++) {
      const operationOfferingId = operation.getIn(['value', valueIndex, 'offeringId'], null);
      const operationCombination = operation.getIn(['value', valueIndex, 'combination'], null);

      if (operationCombination === null && operationOfferingId === null) {
        const productInventory = updateInventory(product.get(FORM.INVENTORY), valueIndex);

        if (productInventory === ERROR) return source;

        if (productInventory !== NO_CHANGES) {
          if (shouldClearErrors) {
            product = clearErrors({
              errors: attributes,
              path: FORM.INVENTORY,
              product: product.set(FORM.INVENTORY, productInventory),
            });
          } else {
            product = product.set(FORM.INVENTORY, productInventory);
          }
        }

        if (size) {
          for (let combinationIndex = 0; combinationIndex < size; combinationIndex++) {
            const combination = updateCombination(
              product.getIn([FORM.VARIATIONS, COMBINATIONS, combinationIndex]),
              valueIndex
            );

            if (combination === ERROR) return source;

            if (combination === NO_CHANGES) continue;

            if (shouldClearErrors) {
              product = clearVariationsCombinationErrors({
                errors: attributes,
                combinationIndex,
                product: product.setIn([FORM.VARIATIONS, COMBINATIONS, combinationIndex], combination),
              });
            } else {
              product = product.setIn([FORM.VARIATIONS, COMBINATIONS, combinationIndex], combination);
            }
          }
        }
      }
    }

    return product;
  }

  function inlineEdit() {
    const hasVariations = getSize(source.getIn([FORM.VARIATIONS, COMBINATIONS])) > 0;

    function updateInventory({ field, product, value }) {
      function updatePriceValue({ type }) {
        const initial = product.getIn([FORM.INVENTORY, type, VALUE]) || AMOUNT.ZERO_CENT;
        const updated = getPriceValue({
          absolute: true,
          operationType: 'set',
          operationValue: value,
          productValue: initial,
        });

        if (getError({ channel, type, value: updated })) return product;

        return clearErrors({
          errors: [type],
          path: FORM.INVENTORY,
          product: product.setIn([FORM.INVENTORY, type, VALUE], updated),
        });
      }

      switch (field) {
        case 'shopify_barcode': {
          if (getError({ channel, type: ATTRIBUTE.BARCODE, value })) return product;

          return clearErrors({
            errors: [ATTRIBUTE.BARCODE],
            path: FORM.INVENTORY,
            product: product.setIn([FORM.INVENTORY, ATTRIBUTE.BARCODE, VALUE], value),
          });
        }

        case 'shopify_compare_at_price': {
          return updatePriceValue({ type: ATTRIBUTE.CAP });
        }

        case 'shopify_cost': {
          return updatePriceValue({ type: ATTRIBUTE.CPI });
        }

        case 'shopify_country_code_of_origin': {
          return product.setIn([FORM.INVENTORY, ATTRIBUTE.COUNTRY_CODE, VALUE], value || null);
        }

        case 'shopify_harmonized_system_code': {
          return product.setIn([FORM.INVENTORY, ATTRIBUTE.HS_CODE, VALUE], value || null);
        }

        case 'shopify_inventory_management': {
          if (value === SHOPIFY) {
            if (
              getError({
                channel,
                type: ATTRIBUTE.QUANTITY,
                value: product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, VALUE]),
              })
            ) {
              return product;
            }

            return clearErrors({
              errors: [ATTRIBUTE.QUANTITY],
              path: FORM.INVENTORY,
              product: product.setIn([FORM.INVENTORY, ATTRIBUTE.TRACK_QUANTITY, VALUE], true),
            });
          }

          return clearErrors({
            errors: [ATTRIBUTE.QUANTITY],
            path: FORM.INVENTORY,
            product: product.setIn([FORM.INVENTORY, ATTRIBUTE.TRACK_QUANTITY, VALUE], false),
          });
        }

        case 'shopify_inventory_policy': {
          if (!product.getIn([FORM.INVENTORY, ATTRIBUTE.TRACK_QUANTITY, VALUE])) return product;

          return product.setIn([FORM.INVENTORY, ATTRIBUTE.CONTINUE_SELLING, VALUE], value === 'continue');
        }

        case 'shopify_inventory_quantity': {
          if (!product.getIn([FORM.INVENTORY, ATTRIBUTE.TRACK_QUANTITY, VALUE])) return product;

          if (getError({ channel, type: ATTRIBUTE.QUANTITY, value })) return product;

          return clearErrors({
            errors: [ATTRIBUTE.QUANTITY],
            path: FORM.INVENTORY,
            product: product.setIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, VALUE], value),
          });
        }

        case 'shopify_price': {
          return updatePriceValue({ type: ATTRIBUTE.PRICE });
        }

        case 'shopify_requires_shipping': {
          if (value) {
            if (
              getError({
                channel,
                type: ATTRIBUTE.WEIGHT,
                value: product.getIn([FORM.INVENTORY, ATTRIBUTE.WEIGHT, VALUE]),
              })
            ) {
              return product;
            }

            return clearErrors({
              errors: [ATTRIBUTE.WEIGHT],
              path: FORM.INVENTORY,
              product: product.setIn([FORM.INVENTORY, ATTRIBUTE.PHYSICAL, VALUE], true),
            });
          }

          return clearErrors({
            errors: [ATTRIBUTE.WEIGHT],
            path: FORM.INVENTORY,
            product: product.setIn([FORM.INVENTORY, ATTRIBUTE.PHYSICAL, VALUE], false),
          });
        }

        case 'shopify_sku': {
          if (getError({ channel, type: ATTRIBUTE.SKU, value })) return product;

          return clearErrors({
            errors: [ATTRIBUTE.SKU],
            path: FORM.INVENTORY,
            product: product.setIn([FORM.INVENTORY, ATTRIBUTE.SKU, VALUE], value),
          });
        }

        case 'shopify_taxable': {
          return product.setIn([FORM.INVENTORY, ATTRIBUTE.CHARGE_TAX, VALUE], value || false);
        }

        case 'shopify_weight': {
          if (!product.getIn([FORM.INVENTORY, ATTRIBUTE.PHYSICAL, VALUE])) return product;

          if (getError({ channel, type: ATTRIBUTE.WEIGHT, value })) return product;

          return clearErrors({
            errors: [ATTRIBUTE.WEIGHT],
            path: FORM.INVENTORY,
            product: product.setIn([FORM.INVENTORY, ATTRIBUTE.WEIGHT, VALUE], value),
          });
        }

        case 'shopify_weight_unit': {
          if (!product.getIn([FORM.INVENTORY, ATTRIBUTE.PHYSICAL, VALUE])) return product;

          return product.setIn([FORM.INVENTORY, ATTRIBUTE.UNIT, VALUE], value);
        }

        default: {
          return product;
        }
      }
    }

    function updateCombination({ field, index, product, value }) {
      function updatePriceValue({ type }) {
        const initial = product.getIn([FORM.VARIATIONS, COMBINATIONS, index, type]) || AMOUNT.ZERO_CENT;
        const updated = getPriceValue({
          absolute: true,
          operationType: 'set',
          operationValue: value,
          productValue: initial,
        });

        if (getError({ channel, type, value: updated })) return product;

        return clearVariationsCombinationErrors({
          errors: [type],
          combinationIndex: index,
          product: product.setIn([FORM.VARIATIONS, COMBINATIONS, index, type], updated),
        });
      }

      switch (field) {
        case 'shopify_barcode': {
          if (getError({ channel, type: ATTRIBUTE.BARCODE, value })) return product;

          return clearVariationsCombinationErrors({
            errors: [ATTRIBUTE.BARCODE],
            combinationIndex: index,
            product: product.setIn([FORM.VARIATIONS, COMBINATIONS, index, ATTRIBUTE.BARCODE], value),
          });
        }

        case 'shopify_compare_at_price': {
          return updatePriceValue({ type: ATTRIBUTE.CAP });
        }

        case 'shopify_cost': {
          return updatePriceValue({ type: ATTRIBUTE.CPI });
        }

        case 'shopify_harmonized_system_code': {
          return product.setIn([FORM.VARIATIONS, COMBINATIONS, index, ATTRIBUTE.HS_CODE], value || null);
        }

        case 'shopify_inventory_management': {
          if (value === SHOPIFY) {
            if (
              getError({
                channel,
                type: ATTRIBUTE.QUANTITY,
                value: product.getIn([FORM.VARIATIONS, COMBINATIONS, index, ATTRIBUTE.QUANTITY]),
              })
            ) {
              return product;
            }

            return clearVariationsCombinationErrors({
              errors: [ATTRIBUTE.QUANTITY],
              combinationIndex: index,
              product: product.setIn([FORM.VARIATIONS, COMBINATIONS, index, ATTRIBUTE.TRACK_QUANTITY], true),
            });
          }

          return clearVariationsCombinationErrors({
            errors: [ATTRIBUTE.QUANTITY],
            combinationIndex: index,
            product: product
              .setIn([FORM.VARIATIONS, COMBINATIONS, index, ATTRIBUTE.QUANTITY], AMOUNT.ZERO)
              .setIn([FORM.VARIATIONS, COMBINATIONS, index, ATTRIBUTE.TRACK_QUANTITY], false),
          });
        }

        case 'shopify_inventory_policy': {
          if (!product.setIn([FORM.VARIATIONS, COMBINATIONS, index, ATTRIBUTE.TRACK_QUANTITY])) return product;

          return product.setIn([FORM.VARIATIONS, COMBINATIONS, index, ATTRIBUTE.CONTINUE_SELLING], value === 'continue');
        }

        case 'shopify_inventory_quantity': {
          if (!product.setIn([FORM.VARIATIONS, COMBINATIONS, index, ATTRIBUTE.TRACK_QUANTITY])) return product;

          if (getError({ channel, type: ATTRIBUTE.QUANTITY, value })) return product;

          return clearVariationsCombinationErrors({
            errors: [ATTRIBUTE.QUANTITY],
            combinationIndex: index,
            product: product.setIn([FORM.VARIATIONS, COMBINATIONS, index, ATTRIBUTE.QUANTITY], value),
          });
        }

        case 'shopify_price': {
          return updatePriceValue({ type: ATTRIBUTE.PRICE });
        }

        case 'shopify_requires_shipping': {
          if (value) {
            if (
              getError({
                channel,
                type: ATTRIBUTE.WEIGHT,
                value: product.getIn([FORM.VARIATIONS, COMBINATIONS, index, ATTRIBUTE.WEIGHT]),
              })
            ) {
              return product;
            }

            return clearVariationsCombinationErrors({
              errors: [ATTRIBUTE.WEIGHT],
              combinationIndex: index,
              product: product.setIn([FORM.VARIATIONS, COMBINATIONS, index, ATTRIBUTE.PHYSICAL], true),
            });
          }

          return clearVariationsCombinationErrors({
            errors: [ATTRIBUTE.WEIGHT],
            combinationIndex: index,
            product: product.setIn([FORM.VARIATIONS, COMBINATIONS, index, ATTRIBUTE.PHYSICAL], false),
          });
        }

        case 'shopify_sku': {
          if (getError({ channel, type: ATTRIBUTE.SKU, value })) return product;

          return clearVariationsCombinationErrors({
            errors: [ATTRIBUTE.SKU],
            combinationIndex: index,
            product: product.setIn([FORM.VARIATIONS, COMBINATIONS, index, ATTRIBUTE.SKU], value),
          });
        }

        case 'shopify_taxable': {
          return product.setIn([FORM.VARIATIONS, COMBINATIONS, index, ATTRIBUTE.CHARGE_TAX], value || false);
        }

        case 'shopify_weight': {
          if (!product.getIn([FORM.VARIATIONS, COMBINATIONS, index, ATTRIBUTE.PHYSICAL])) return product;

          if (getError({ channel, type: ATTRIBUTE.WEIGHT, value })) return product;

          return clearVariationsCombinationErrors({
            errors: [ATTRIBUTE.WEIGHT],
            combinationIndex: index,
            product: product.setIn([FORM.VARIATIONS, COMBINATIONS, index, ATTRIBUTE.WEIGHT], value),
          });
        }

        case 'shopify_weight_unit': {
          if (!product.getIn([FORM.VARIATIONS, COMBINATIONS, index, ATTRIBUTE.PHYSICAL])) return product;

          return product.setIn([FORM.VARIATIONS, COMBINATIONS, index, ATTRIBUTE.UNIT], value);
        }

        default: {
          return product;
        }
      }
    }

    function reduceValues(reducedByValues, change) {
      const fields = change.get('fields');
      const index = change.get('index');

      function reduceFields(reducedProduct, value, field) {
        return hasVariations
          ? updateCombination({ field, index, product: reducedProduct, value })
          : index === 0
            ? updateInventory({ field, product: reducedProduct, value })
            : reducedProduct;
      }

      return fields.reduce(reduceFields, reducedByValues);
    }

    return operation.get('value').reduce(reduceValues, source);
  }

  switch (operationType) {
    case OPERATIONS[SHOPIFY].BARCODE.ADD_AFTER:
    case OPERATIONS[SHOPIFY].SKU.ADD_AFTER: {
      const type = getAttributeByOperation(operationType);
      return bulkEdit({
        attributes: [type],
        updateInventory(productInventory, valueIndex) {
          const productValue = productInventory.getIn([type, VALUE]) || '';
          const operationValue = operation.getIn(['value', valueIndex, 'value']);
          const value = `${productValue}${operationValue}`;

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type, value })
            ? ERROR
            : productInventory.setIn([type, VALUE], value);
        },
        updateCombination(combination, valueIndex) {
          const productValue = combination.get(type) || '';
          const operationValue = operation.getIn(['value', valueIndex, 'value']);
          const value = `${productValue}${operationValue}`;

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type, value })
            ? ERROR
            : combination.set(type, value);
        },
      });
    }

    case OPERATIONS[SHOPIFY].BARCODE.ADD_BEFORE:
    case OPERATIONS[SHOPIFY].SKU.ADD_BEFORE: {
      const type = getAttributeByOperation(operationType);
      return bulkEdit({
        attributes: [type],
        updateInventory(productInventory, valueIndex) {
          const productValue = productInventory.getIn([type, VALUE]) || '';
          const operationValue = operation.getIn(['value', valueIndex, 'value']);
          const value = `${operationValue}${productValue}`;

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type, value })
            ? ERROR
            : productInventory.setIn([type, VALUE], value);
        },
        updateCombination(combination, valueIndex) {
          const productValue = combination.get(type) || '';
          const operationValue = operation.getIn(['value', valueIndex, 'value']);
          const value = `${operationValue}${productValue}`;

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type, value })
            ? ERROR
            : combination.set(type, value);
        },
      });
    }

    case OPERATIONS[SHOPIFY].BARCODE.CHANGE_TO:
    case OPERATIONS[SHOPIFY].SKU.CHANGE_TO: {
      const type = getAttributeByOperation(operationType);
      return bulkEdit({
        attributes: [type],
        updateInventory(productInventory, valueIndex) {
          const productValue = productInventory.getIn([type, VALUE]) || '';
          const value = operation.getIn(['value', valueIndex, 'value']);

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type, value })
            ? ERROR
            : productInventory.setIn([type, VALUE], value);
        },
        updateCombination(combination, valueIndex) {
          const productValue = combination.get(type) || '';
          const value = operation.getIn(['value', valueIndex, 'value']);

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type, value })
            ? ERROR
            : combination.set(type, value);
        },
      });
    }

    case OPERATIONS[SHOPIFY].BARCODE.DELETE:
    case OPERATIONS[SHOPIFY].SKU.DELETE: {
      const type = getAttributeByOperation(operationType);
      return bulkEdit({
        attributes: [type],
        updateInventory(productInventory, valueIndex) {
          const productValue = productInventory.getIn([type, VALUE]);

          if (!productValue) return NO_CHANGES;

          const operationValue = operation.getIn(['value', valueIndex, 'value']);
          const pattern = new RegExp(escapeRegex(operationValue), 'ig');
          const value = productValue.replace(pattern, '');

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type, value })
            ? ERROR
            : productInventory.setIn([type, VALUE], value);
        },
        updateCombination(combination, valueIndex) {
          const productValue = combination.get(type);

          if (!productValue) return NO_CHANGES;

          const operationValue = operation.getIn(['value', valueIndex, 'value']);
          const pattern = new RegExp(escapeRegex(operationValue), 'ig');
          const value = productValue.replace(pattern, '');

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type, value })
            ? ERROR
            : combination.set(type, value);
        },
      });
    }

    case OPERATIONS[SHOPIFY].BARCODE.FIND_AND_REPLACE:
    case OPERATIONS[SHOPIFY].SKU.FIND_AND_REPLACE: {
      const type = getAttributeByOperation(operationType);
      return bulkEdit({
        attributes: [type],
        updateInventory(productInventory, valueIndex) {
          const productValue = productInventory.getIn([type, VALUE]);

          if (!productValue) return NO_CHANGES;

          const find = operation.getIn(['value', valueIndex, 'value', 'find']);
          const replace = operation.getIn(['value', valueIndex, 'value', 'replace']);
          const pattern = new RegExp(escapeRegex(find), 'ig');
          const value = productValue.replace(pattern, replace);

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type, value })
            ? ERROR
            : productInventory.setIn([type, VALUE], value);
        },
        updateCombination(combination, valueIndex) {
          const productValue = combination.get(type);

          if (!productValue) return NO_CHANGES;

          const find = operation.getIn(['value', valueIndex, 'value', 'find']);
          const replace = operation.getIn(['value', valueIndex, 'value', 'replace']);
          const pattern = new RegExp(escapeRegex(find), 'ig');
          const value = productValue.replace(pattern, replace);

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type, value })
            ? ERROR
            : combination.set(type, value);
        },
      });
    }

    case OPERATIONS[SHOPIFY].CAP.CHANGE_TO:
    case OPERATIONS[SHOPIFY].CAP.DECREASE_BY:
    case OPERATIONS[SHOPIFY].CAP.INCREASE_BY:
    case OPERATIONS[SHOPIFY].CPI.CHANGE_TO:
    case OPERATIONS[SHOPIFY].CPI.DECREASE_BY:
    case OPERATIONS[SHOPIFY].CPI.INCREASE_BY:
    case OPERATIONS[SHOPIFY].PRICE.CHANGE_TO:
    case OPERATIONS[SHOPIFY].PRICE.DECREASE_BY:
    case OPERATIONS[SHOPIFY].PRICE.INCREASE_BY: {
      const type = getAttributeByOperation(operationType);
      return bulkEdit({
        attributes: [type],
        updateInventory(productInventory, valueIndex) {
          const absolute = operation.getIn(['value', valueIndex, 'value', 'type']) === PRICE_TYPE.ABSOLUTE;
          const cents = operation.getIn(['value', valueIndex, 'value', 'rounding']);
          const operationValue = operation.getIn(['value', valueIndex, 'value', 'value']) || AMOUNT.ZERO_CENT;
          const productValue = productInventory.getIn([type, VALUE]) || AMOUNT.ZERO_CENT;
          const value = getPriceValue({ absolute, cents, operationType, operationValue, productValue });

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type, value })
            ? ERROR
            : productInventory.setIn([type, VALUE], value);
        },
        updateCombination(combination, valueIndex) {
          const absolute = operation.getIn(['value', valueIndex, 'value', 'type']) === PRICE_TYPE.ABSOLUTE;
          const cents = operation.getIn(['value', valueIndex, 'value', 'rounding']);
          const operationValue = operation.getIn(['value', valueIndex, 'value', 'value']) || AMOUNT.ZERO_CENT;
          const productValue = combination.get(type) || AMOUNT.ZERO_CENT;
          const value = getPriceValue({ absolute, cents, operationType, operationValue, productValue });

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type, value })
            ? ERROR
            : combination.set(type, value);
        },
      });
    }

    case OPERATIONS[SHOPIFY].CAP.COPY_FROM_PRICE:
    case OPERATIONS[SHOPIFY].PRICE.COPY_FROM_CAP: {
      const type = getAttributeByOperation(operationType);
      const attribute = type === ATTRIBUTE.CAP ? ATTRIBUTE.PRICE : ATTRIBUTE.CAP;
      return bulkEdit({
        updateInventory(productInventory) {
          const productValue = productInventory.getIn([type, VALUE]) || AMOUNT.ZERO_CENT;
          const value = productInventory.getIn([attribute, VALUE]);
          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type, value })
            ? ERROR
            : productInventory.setIn([type, VALUE], value);
        },
        updateCombination(combination) {
          const productValue = combination.get(type) || AMOUNT.ZERO_CENT;
          const value = combination.get(attribute) || AMOUNT.ZERO_CENT;

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type, value })
            ? ERROR
            : combination.set(type, value);
        },
      });
    }

    case OPERATIONS[SHOPIFY].CHARGE_TAX.SET: {
      return bulkEdit({
        updateInventory(productInventory, valueIndex) {
          const productValue = productInventory.getIn([ATTRIBUTE.CHARGE_TAX, VALUE]);
          const value = operation.getIn(['value', valueIndex, 'value', 'taxable']);

          if (value === productValue) return NO_CHANGES;

          return productInventory.setIn([ATTRIBUTE.CHARGE_TAX, VALUE], value);
        },
        updateCombination(combination, valueIndex) {
          const productValue = combination.get(ATTRIBUTE.CHARGE_TAX);
          const value = operation.getIn(['value', valueIndex, 'value', 'taxable']);

          if (value === productValue) return NO_CHANGES;

          return combination.set(ATTRIBUTE.CHARGE_TAX, value);
        },
      });
    }

    case OPERATIONS[SHOPIFY].CONTINUE_SELLING.SET: {
      return bulkEdit({
        updateInventory(productInventory, valueIndex) {
          const productValue = productInventory.getIn([ATTRIBUTE.CONTINUE_SELLING, VALUE]);
          const value = operation.getIn(['value', valueIndex, 'value', 'inventoryPolicy']) === 'continue';

          if (value === productValue) return NO_CHANGES;

          return productInventory.setIn([ATTRIBUTE.CONTINUE_SELLING, VALUE], value);
        },
        updateCombination(combination, valueIndex) {
          const productValue = combination.get(ATTRIBUTE.CONTINUE_SELLING);
          const value = operation.getIn(['value', valueIndex, 'value', 'inventoryPolicy']) === 'continue';

          if (value === productValue) return NO_CHANGES;

          return combination.set(ATTRIBUTE.CONTINUE_SELLING, value);
        },
      });
    }

    case OPERATIONS[SHOPIFY].COUNTRY_CODE.SET:
    case OPERATIONS[SHOPIFY].HS_CODE.SET: {
      const type = getAttributeByOperation(operationType);
      return bulkEdit({
        updateInventory(productInventory, valueIndex) {
          const productValue = productInventory.getIn([type, VALUE]);
          const value = operation.getIn(['value', valueIndex, 'value', type]);

          if (value === productValue) return NO_CHANGES;

          return productInventory.setIn([type, VALUE], value);
        },
        updateCombination(combination, valueIndex) {
          const productValue = combination.get(type);
          const value = operation.getIn(['value', valueIndex, 'value', type]);

          if (value === productValue) return NO_CHANGES;

          return combination.set(type, value);
        },
      });
    }

    case OPERATIONS[SHOPIFY].INVENTORY_INLINE_EDIT: {
      return inlineEdit();
    }

    case OPERATIONS[SHOPIFY].PHYSICAL.SET: {
      return bulkEdit({
        attributes: [ATTRIBUTE.WEIGHT],
        updateInventory(productInventory, valueIndex) {
          const productValue = productInventory.getIn([ATTRIBUTE.PHYSICAL, VALUE]);
          const value = operation.getIn(['value', valueIndex, 'value', 'requiresShipping']);

          if (value === productValue) return NO_CHANGES;

          return productInventory.setIn([ATTRIBUTE.PHYSICAL, VALUE], value);
        },
        updateCombination(combination, valueIndex) {
          const productValue = combination.get(ATTRIBUTE.PHYSICAL);
          const value = operation.getIn(['value', valueIndex, 'value', 'requiresShipping']);

          if (value === productValue) return NO_CHANGES;

          return combination.set(ATTRIBUTE.PHYSICAL, value);
        },
      });
    }

    case OPERATIONS[SHOPIFY].QUANTITY.CHANGE_TO:
    case OPERATIONS[SHOPIFY].QUANTITY.DECREASE_BY:
    case OPERATIONS[SHOPIFY].QUANTITY.INCREASE_BY: {
      return bulkEdit({
        attributes: [ATTRIBUTE.QUANTITY],
        updateInventory(productInventory, valueIndex) {
          if (!productInventory.getIn([ATTRIBUTE.TRACK_QUANTITY, VALUE])) {
            return NO_CHANGES;
          }

          const operationValue = operation.getIn(['value', valueIndex, 'value']) || AMOUNT.ZERO;
          const productValue = productInventory.getIn([ATTRIBUTE.QUANTITY, VALUE]) || AMOUNT.ZERO;
          const value = getNumberValue({ decimals: 0, operationType, operationValue, productValue });

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.QUANTITY, value })
            ? ERROR
            : productInventory.setIn([ATTRIBUTE.QUANTITY, VALUE], value);
        },
        updateCombination(combination, valueIndex) {
          if (!combination.get(ATTRIBUTE.TRACK_QUANTITY)) return NO_CHANGES;

          const operationValue = operation.getIn(['value', valueIndex, 'value']) || AMOUNT.ZERO;
          const productValue = combination.get(ATTRIBUTE.QUANTITY) || AMOUNT.ZERO;
          const value = getNumberValue({ decimals: 0, operationType, operationValue, productValue });

          if (value === productValue) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.QUANTITY, value })
            ? ERROR
            : combination.set(ATTRIBUTE.QUANTITY, value);
        },
      });
    }

    case OPERATIONS[SHOPIFY].TRACK_QUANTITY.SET: {
      return bulkEdit({
        attributes: [ATTRIBUTE.QUANTITY],
        updateInventory(productInventory, valueIndex) {
          const productValue = productInventory.getIn([ATTRIBUTE.TRACK_QUANTITY, VALUE]);
          const value = operation.getIn(['value', valueIndex, 'value', 'inventoryManagement']) === SHOPIFY;

          if (value === productValue) return NO_CHANGES;

          return value
            ? getError({
              channel,
              type: ATTRIBUTE.QUANTITY,
              value: productInventory.getIn([ATTRIBUTE.QUANTITY, VALUE]),
            })
              ? ERROR
              : productInventory.setIn([ATTRIBUTE.TRACK_QUANTITY, VALUE], value)
            : productInventory.setIn([ATTRIBUTE.TRACK_QUANTITY, VALUE], value);
        },
        updateCombination(combination, valueIndex) {
          const productValue = combination.get(ATTRIBUTE.TRACK_QUANTITY);
          const value = operation.getIn(['value', valueIndex, 'value', 'inventoryManagement']) === SHOPIFY;

          if (value === productValue) return NO_CHANGES;

          return value
            ? getError({ channel, type: ATTRIBUTE.QUANTITY, value: combination.get(ATTRIBUTE.QUANTITY) })
              ? ERROR
              : combination.set(ATTRIBUTE.TRACK_QUANTITY, value)
            : combination.set(ATTRIBUTE.TRACK_QUANTITY, value);
        },
      });
    }

    case OPERATIONS[SHOPIFY].WEIGHT.CHANGE_TO:
    case OPERATIONS[SHOPIFY].WEIGHT.INCREASE_BY:
    case OPERATIONS[SHOPIFY].WEIGHT.DECREASE_BY: {
      return bulkEdit({
        attributes: [ATTRIBUTE.WEIGHT],
        updateInventory(productInventory, valueIndex) {
          if (!productInventory.getIn([ATTRIBUTE.PHYSICAL, VALUE])) return NO_CHANGES;

          const operationValue = operation.getIn(['value', valueIndex, 'value', ATTRIBUTE.WEIGHT]) || AMOUNT.ZERO;
          const productValue = productInventory.getIn([ATTRIBUTE.WEIGHT, VALUE]) || AMOUNT.ZERO;
          const weight = getNumberValue({ operationType, operationValue, productValue });
          const unit = operation.getIn(['value', valueIndex, 'value', ATTRIBUTE.UNIT]);

          if (
            weight === productValue &&
            unit === productInventory.getIn([ATTRIBUTE.UNIT, VALUE])
          ) {
            return NO_CHANGES;
          }

          return getError({ channel, type: ATTRIBUTE.WEIGHT, value: weight })
            ? ERROR
            : productInventory
              .setIn([ATTRIBUTE.UNIT, VALUE], unit)
              .setIn([ATTRIBUTE.WEIGHT, VALUE], weight);
        },
        updateCombination(combination, valueIndex) {
          if (!combination.get(ATTRIBUTE.PHYSICAL)) return NO_CHANGES;

          const operationValue = operation.getIn(['value', valueIndex, 'value', ATTRIBUTE.WEIGHT]) || AMOUNT.ZERO;
          const productValue = combination.get(ATTRIBUTE.WEIGHT) || AMOUNT.ZERO;
          const weight = getNumberValue({ operationType, operationValue, productValue });
          const unit = operation.getIn(['value', valueIndex, 'value', ATTRIBUTE.UNIT]);

          if (weight === productValue && unit === combination.get(ATTRIBUTE.UNIT)) return NO_CHANGES;

          return getError({ channel, type: ATTRIBUTE.WEIGHT, value: weight })
            ? ERROR
            : combination.set(ATTRIBUTE.WEIGHT, weight).set(ATTRIBUTE.UNIT, unit);
        },
      });
    }

    default: {
      return source;
    }
  }
}

export default function inventory({ actions, operation, product }) {
  switch (product.get('channel')) {
    case EBAY: {
      return { actions, product: updateEbayInventory({ operation, product }) };
    }

    case ETSY: {
      return { actions, product: updateEtsyInventory({ operation, product }) };
    }

    case FAIRE: {
      return { actions, product: updateFaireInventory({ operation, product }) };
    }

    case SHOPIFY: {
      return { actions, product: updateShopifyInventory({ operation, product }) };
    }

    default: {
      return { actions, product };
    }
  }
}
